import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  FileText,
  FileSignature,
  MessageSquare,
  Pencil,
  Save,
  CheckCircle2,
  XCircle,
  Clock,
  UserPlus,
  UserCheck,
  Copy,
} from "lucide-react";
import { useAuthStore } from "@/features/auth/store";
import { TextSignActionBar } from "./actions";
import { TextSignDiscussions } from "./discussions";
import { SignerPickerDialog, SIGN_ROLES as SIGN_ROLE_DEFS } from "./SignerPicker";
import { PageHeader } from "@/components/ui/PageHeader";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/Modal";
import { FilePreviewModal } from "@/components/ui/FilePreviewModal";
import { ReviewPanel } from "@/components/ai/ReviewPanel";
import { SummaryButton } from "@/components/ai/SummaryButton";
import { fetchTextSign } from "@/features/text-sign/api";
import {
  stateLabel,
  TEXT_PROCESS_STATE_LABEL,
  SIGN_ROLE_LABEL,
} from "@/features/text-sign/helpers";
import { api } from "@/lib/api/client";
import { cn } from "@/lib/utils";

export function TextSignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const numId = Number(id);
  const currentUser = useAuthStore((s) => s.user);
  const qc = useQueryClient();

  const [previewFile, setPreviewFile] = useState<{
    fileId: number;
    fileName: string | null;
    filePath?: string | null;
    source?: "files" | "attach";
  } | null>(null);
  const [discussionOpen, setDiscussionOpen] = useState(false);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  const [tplPerm, setTplPerm] = useState(0);
  const [saveTplLoading, setSaveTplLoading] = useState(false);
  const [saveTplError, setSaveTplError] = useState<string | null>(null);

  const [signerDialogOpen, setSignerDialogOpen] = useState(false);
  const [signerDialogMode, setSignerDialogMode] = useState<"internal" | "bypass">("internal");

  const { data, isLoading } = useQuery({
    queryKey: ["text-sign", numId],
    queryFn: () => fetchTextSign(numId),
    enabled: Number.isFinite(numId) && numId > 0,
  });

  if (isLoading) return <div className="text-muted-foreground">Đang tải…</div>;
  if (!data) return <div className="text-muted-foreground">Không tìm thấy văn bản.</div>;

  const s = stateLabel(data.state);
  const isCreator = data.creatorId === currentUser?.staffId;
  const canEdit = data.state === 0 && isCreator;

  async function saveTemplate() {
    if (!data) return;
    if (!tplName.trim()) {
      setSaveTplError("Cần nhập tên mẫu");
      return;
    }
    setSaveTplLoading(true);
    setSaveTplError(null);
    try {
      await api.post("/text-sign/templates", {
        name: tplName.trim(),
        permission: tplPerm,
        signers: data.trail
          .filter((t) => t.receiverId != null)
          .map((t) => ({
            staffId: t.receiverId as number,
            role: t.receiverRole ?? SIGN_ROLE_DEFS[0].id,
            level: t.signLevel ?? 1,
            parallelPosition: t.parallelSignPosition ?? null,
          })),
      });
      setSaveTplOpen(false);
      setTplName("");
      setTplPerm(0);
    } catch (e) {
      setSaveTplError((e as Error).message);
    } finally {
      setSaveTplLoading(false);
    }
  }

  return (
    <div>
      <PageHeader
        title={data.title ?? "Văn bản không tiêu đề"}
        breadcrumb={`Văn bản Ký điện tử › Xem thông tin chi tiết #${data.textId}`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="md" onClick={() => nav(-1)}>
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Quay lại
            </Button>
            {canEdit && (
              <Button variant="outline" size="md" onClick={() => nav(`/signs/${data.textId}/edit`)}>
                <Pencil className="h-4 w-4 mr-1.5" /> Sửa nháp
              </Button>
            )}
            <TextSignActionBar
              textId={data.textId}
              state={data.state}
              creatorId={data.creatorId}
              trail={data.trail}
              onChanged={() => qc.invalidateQueries({ queryKey: ["text-sign", numId] })}
            />
          </div>
        }
      />

      <Card className="mb-4">
        <CardHeader className="flex items-center justify-between">
          <CardTitle>Thông tin văn bản</CardTitle>
          <StatusBadge variant={s.variant}>{s.label}</StatusBadge>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-3 text-sm">
            <Row label="Tên văn bản" value={data.title} />
            <Row label="Mã văn bản" value={data.code} />
            <Row label="Nội dung" value={data.description} wide />
            <Row
              label="Trạng thái văn bản"
              value={<StatusBadge variant={s.variant}>{s.label}</StatusBadge>}
            />
            <Row label="Lĩnh vực" value={data.areaName ?? "—"} />
            <Row label="Thể thức" value={data.typeName ?? "—"} />
            <Row label="Người trình ký" value={data.creatorName} />
            <Row label="Ngày tạo" value={data.createDate} />
            <Row label="Đơn vị trình ký" value={data.createGroupName} />
            <Row label="Đơn vị ban hành" value={data.publishOrgName ?? data.createGroupName} />
            <Row label="Độ mật" value={data.stypeName ?? "—"} />
            <Row label="Độ khẩn" value={data.priorityName ?? "—"} />
            <Row
              label="Ban hành tự động"
              value={
                <label className="inline-flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={data.autoPublish ?? false}
                    disabled
                    className="h-4 w-4 rounded border-input accent-primary-600"
                  />
                  <span className="text-muted-foreground">{data.autoPublish ? "Có" : "Không"}</span>
                </label>
              }
            />
            {data.contractNumber && (
              <Row label="Số hợp đồng/Số hồ sơ bồi thường" value={data.contractNumber} />
            )}
            {data.mainFile && (
              <Row
                label="File ký"
                value={
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 text-primary-600 hover:underline"
                    onClick={() =>
                      setPreviewFile({
                        fileId: data.mainFile!.fileId,
                        fileName: data.mainFile!.fileName,
                        filePath: data.mainFile!.filePath,
                        source: data.mainFile!.source ?? "attach",
                      })
                    }
                  >
                    <FileSignature className="h-3.5 w-3.5" />
                    {data.mainFile.fileName ?? `File #${data.mainFile.fileId}`}
                  </button>
                }
              />
            )}
            {data.attachments.length > 0 && (
              <Row
                label={`File đính kèm (${data.attachments.length})`}
                value={
                  <ul className="space-y-1">
                    {data.attachments.map((a) => (
                      <li key={a.fileId}>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 text-primary-600 hover:underline text-sm"
                          onClick={() =>
                            setPreviewFile({
                              fileId: a.fileId,
                              fileName: a.fileName,
                              filePath: a.filePath,
                              source: a.source ?? "attach",
                            })
                          }
                        >
                          <FileText className="h-3.5 w-3.5" />
                          {a.fileName ?? `File #${a.fileId}`}
                        </button>
                      </li>
                    ))}
                  </ul>
                }
                wide
              />
            )}
          </div>

          {/* Kết quả chấm thể thức + tóm tắt AI — CHỈ tham khảo, không chặn trình ký/ký duyệt. */}
          {data.mainFile?.fileId && (
            <div className="mt-4 space-y-3">
              <SummaryButton attachId={data.mainFile.fileId} textId={data.textId} />
              <ReviewPanel attachId={data.mainFile.fileId} />
            </div>
          )}
        </CardBody>
      </Card>

      <Card className="mb-4">
        <CardHeader className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle>Danh sách ký duyệt</CardTitle>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setSignerDialogMode("internal");
                setSignerDialogOpen(true);
              }}
            >
              <UserPlus className="h-4 w-4 mr-1.5" /> Bổ sung người ký nội bộ
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setSignerDialogMode("bypass");
                setSignerDialogOpen(true);
              }}
            >
              <UserCheck className="h-4 w-4 mr-1.5" /> Bổ sung người ký vượt cấp
            </Button>
          </div>
        </CardHeader>
        <CardBody className="p-4 sm:p-5">
          {data.trail.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-6">Chưa có luồng ký.</div>
          ) : (
            <SignFlowTimeline trail={data.trail} />
          )}
        </CardBody>
      </Card>

      {data.orgReceivers && data.orgReceivers.length > 0 && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle>Danh sách đơn vị</CardTitle>
          </CardHeader>
          <CardBody className="p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full w-full text-sm">
                <thead>
                  <tr className="bg-muted/40 border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="text-left px-3 py-2 w-10">STT</th>
                    <th className="text-left px-3 py-2">Phòng ban</th>
                  </tr>
                </thead>
                <tbody>
                  {data.orgReceivers.map((o, i) => (
                    <tr key={o.orgId} className="border-b border-border last:border-b-0">
                      <td className="px-3 py-2 tabular-nums text-muted-foreground">{i + 1}</td>
                      <td className="px-3 py-2">{o.name ?? `#${o.orgId}`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      )}

      {/* OLD-style bottom action row */}
      <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="md"
          onClick={() => setDiscussionOpen(true)}
          className="relative"
        >
          <MessageSquare className="h-4 w-4 mr-1.5" /> Thảo luận
          {(data.discussionCount ?? 0) > 0 && (
            <span className="absolute -top-2 -right-2 h-5 min-w-5 px-1 grid place-items-center rounded-full bg-destructive text-destructive-foreground text-xs font-semibold">
              {data.discussionCount}
            </span>
          )}
        </Button>
        <Button variant="outline" size="md" onClick={() => setSaveTplOpen(true)}>
          <Save className="h-4 w-4 mr-1.5" /> Lưu mẫu luồng ký
        </Button>
        <Button variant="outline" size="md" disabled title="Sắp có">
          <Copy className="h-4 w-4 mr-1.5" /> Chọn mẫu trình ký
        </Button>
      </div>

      <FilePreviewModal
        open={previewFile !== null}
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />

      <Modal
        open={discussionOpen}
        onClose={() => setDiscussionOpen(false)}
        title="Thảo luận văn bản"
        size="lg"
      >
        <DiscussionPanel textId={data.textId} />
      </Modal>

      <Modal
        open={saveTplOpen}
        onClose={() => setSaveTplOpen(false)}
        title="Lưu mẫu luồng ký"
        footer={
          <>
            <Button variant="outline" size="md" onClick={() => setSaveTplOpen(false)}>
              Huỷ
            </Button>
            <Button variant="default" size="md" onClick={saveTemplate} disabled={saveTplLoading}>
              {saveTplLoading ? "Đang lưu…" : "Lưu mẫu"}
            </Button>
          </>
        }
      >
        <label className="block text-sm font-medium mb-1">
          Tên mẫu <span className="text-destructive">*</span>
        </label>
        <input
          className="w-full h-9 px-3 rounded border border-input bg-background text-sm mb-3"
          value={tplName}
          onChange={(e) => setTplName(e.target.value)}
          placeholder="VD: Luồng ký tiêu chuẩn KTCN"
        />
        <label className="block text-sm font-medium mb-1">Phạm vi</label>
        <select
          className="w-full h-9 px-3 rounded border border-input bg-background text-sm"
          value={tplPerm}
          onChange={(e) => setTplPerm(Number(e.target.value))}
        >
          <option value={0}>Chỉ mình tôi</option>
          <option value={1}>Chia sẻ toàn hệ thống</option>
        </select>
        <div className="text-xs text-muted-foreground mt-3">
          Sẽ lưu {data.trail.length} người ký từ luồng hiện tại.
        </div>
        {saveTplError && <div className="text-sm text-destructive mt-2">{saveTplError}</div>}
      </Modal>

      <SignerPickerDialog
        open={signerDialogOpen}
        mode={signerDialogMode}
        defaultOrgId={currentUser?.orgId ?? undefined}
        onClose={() => setSignerDialogOpen(false)}
        onPick={() => {
          // TODO: wire to backend insert-signer endpoint
          setSignerDialogOpen(false);
        }}
        defaultLevel={data.trail.length + 1}
      />
    </div>
  );
}

function Row({ label, value, wide }: { label: string; value: React.ReactNode; wide?: boolean }) {
  return (
    <div
      className={cn(
        "flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-3",
        wide && "lg:col-span-2",
      )}
    >
      <div className="sm:w-[170px] sm:shrink-0 text-xs uppercase tracking-wider text-muted-foreground pt-0.5">
        {label}
      </div>
      <div className="flex-1 text-sm min-w-0 break-words">{value ?? "—"}</div>
    </div>
  );
}

function DiscussionPanel({ textId }: { textId: number }) {
  return <TextSignDiscussions textId={textId} />;
}

type TrailItem = {
  processId: number;
  receiverId: number | null;
  receiverName: string | null;
  receiverPosition?: string | null;
  receiverOrgName?: string | null;
  signatureType?: number | null;
  state: number | null;
  signLevel: number | null;
  receiverRole: number | null;
  sendDate: string | null;
  actionDate: string | null;
  rejected: boolean;
  signImage: boolean;
  signImageId: number | null;
  assignerComment: string | null;
  textComment: string | null;
};

function splitOrgLine(s: string | null | undefined): { position: string; org: string } {
  if (!s) return { position: "", org: "" };
  // vpdt ORG_VHR_NAME looks like "Position - Dept/Team - Company". Keep the first
  // segment as position, rejoin the rest as org.
  const parts = s
    .split(" - ")
    .map((x) => x.trim())
    .filter(Boolean);
  if (parts.length <= 1) return { position: "", org: parts[0] ?? "" };
  const [position, ...rest] = parts;
  return { position, org: rest.join(" - ") };
}

function stepVisuals(t: TrailItem) {
  if (t.rejected || t.state === 2)
    return {
      ring: "ring-destructive/30 bg-destructive/10 text-destructive",
      icon: <XCircle className="h-5 w-5" />,
      label: "Đã từ chối",
      pill: "text-destructive bg-destructive/10",
    };
  if (t.state === 4)
    return {
      ring: "ring-success/30 bg-success/10 text-success",
      icon: <CheckCircle2 className="h-5 w-5" />,
      label: "Đã ký",
      pill: "text-success bg-success/10",
    };
  if (t.state === 0)
    return {
      ring: "ring-primary-300 bg-primary-50 text-primary-600",
      icon: <Clock className="h-5 w-5" />,
      label: "Đang chờ xử lý",
      pill: "text-primary-700 bg-primary-50",
    };
  return {
    ring: "ring-border bg-muted text-muted-foreground",
    icon: <Clock className="h-5 w-5" />,
    label: TEXT_PROCESS_STATE_LABEL[t.state ?? -1]?.label ?? "—",
    pill: "text-muted-foreground bg-muted",
  };
}

function signatureTypeLabel(t: TrailItem): string {
  if (t.signatureType === 2) return "Ký nháy";
  if (t.signatureType === 3) return "Ký duyệt";
  if (t.signatureType === 1) return "Văn thư";
  return SIGN_ROLE_LABEL[t.receiverRole ?? -1] ?? "";
}

function SignFlowTimeline({ trail }: { trail: TrailItem[] }) {
  return (
    <ol className="relative">
      {trail.map((t, i) => {
        const v = stepVisuals(t);
        const { position, org } = splitOrgLine(t.receiverOrgName);
        const posDisplay = position || t.receiverPosition || "";
        const ts = t.actionDate ?? t.sendDate ?? null;
        const note = t.textComment ?? t.assignerComment ?? "";
        const isLast = i === trail.length - 1;
        const sigLabel = signatureTypeLabel(t);
        return (
          <li key={t.processId} className="relative flex gap-4 pb-6 last:pb-0">
            {!isLast && (
              <span aria-hidden className="absolute left-[22px] top-12 bottom-0 w-px bg-border" />
            )}
            <div
              className={cn(
                "relative z-10 mt-1 h-11 w-11 shrink-0 rounded-full ring-2 grid place-items-center font-semibold",
                v.ring,
              )}
            >
              {v.icon}
              <span className="absolute -bottom-1 -right-1 h-5 min-w-5 px-1 grid place-items-center rounded-full bg-background border border-border text-[11px] tabular-nums font-semibold text-foreground">
                {(t.signLevel ?? i) + 1}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className="font-semibold text-sm truncate max-w-full">
                  {t.receiverName?.trim() || `Người ký #${t.receiverId ?? "?"}`}
                </span>
                {sigLabel && (
                  <span
                    className={cn(
                      "text-[11px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded",
                      v.pill,
                    )}
                  >
                    {sigLabel}
                  </span>
                )}
              </div>
              {(posDisplay || org) && (
                <div className="text-xs text-muted-foreground mt-0.5 truncate">
                  {posDisplay}
                  {posDisplay && org ? " · " : ""}
                  {org}
                </div>
              )}
              <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                <span
                  className={cn("inline-flex items-center gap-1 font-medium", v.pill.split(" ")[0])}
                >
                  {v.label}
                </span>
                {ts && (
                  <span className="text-muted-foreground tabular-nums flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {ts}
                  </span>
                )}
                {t.signImage && (
                  <span className="text-muted-foreground flex items-center gap-1">
                    <FileSignature className="h-3 w-3" />
                    Có chữ ký
                  </span>
                )}
              </div>
              {note && (
                <div className="mt-2 text-xs bg-muted/40 border border-border rounded px-2.5 py-1.5 text-foreground whitespace-pre-wrap">
                  {note}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
